import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { canonicalToBrl } from '../../shared/components/MoneyInput';
import { useEstornarRcMovimento } from './hooks';
import type { RcMovimentoItem } from './types';

const MENSAGEM_MAX = 200;

export interface EstornarMovimentoDialogProps {
  open: boolean;
  purchaseRequestId: number;
  movimento: RcMovimentoItem | null;
  onClose: () => void;
}

/**
 * Estorno de um lançamento da RC. O contra-movimento inverte o tipo e mantém a
 * categoria (regra do proc `doRequisicaoCompraLancamentoEstorno`). Comentário é
 * opcional — o backend prefixa "ESTORNO: " na observação.
 */
export function EstornarMovimentoDialog({
  open,
  purchaseRequestId,
  movimento,
  onClose,
}: EstornarMovimentoDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const estornar = useEstornarRcMovimento(purchaseRequestId);
  const [mensagem, setMensagem] = useState('');

  const tipoInvertido = movimento?.tipo === '+' ? '-' : '+';

  async function handleSubmit(): Promise<void> {
    if (!movimento) return;
    try {
      await estornar.mutateAsync({ movimentoId: movimento.id, mensagem: mensagem.trim() });
      toast.success(t('features.requisicoesCompra.extrato.estorno.toastOk'));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setMensagem('') }}
    >
      <DialogTitle>{t('features.requisicoesCompra.extrato.estorno.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {movimento && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                borderLeft: 4,
                borderLeftColor: 'warning.main',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {t('features.requisicoesCompra.extrato.estorno.evidenciaLabel')}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {movimento.categoriaLabel} · {movimento.tipo} R${' '}
                {canonicalToBrl(movimento.valor) || movimento.valor}
              </Typography>
              {movimento.observacao && (
                <Typography variant="body2" color="text.secondary">
                  {movimento.observacao}
                </Typography>
              )}
            </Box>
          )}

          <Alert severity="warning" variant="outlined">
            {t('features.requisicoesCompra.extrato.estorno.aviso', {
              tipo: tipoInvertido,
              valor: movimento ? canonicalToBrl(movimento.valor) || movimento.valor : '',
            })}
          </Alert>

          <TextField
            label={t('features.requisicoesCompra.extrato.estorno.mensagemLabel')}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value.slice(0, MENSAGEM_MAX))}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            helperText={t('features.requisicoesCompra.extrato.estorno.mensagemHelper', {
              used: mensagem.trim().length,
              max: MENSAGEM_MAX,
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={estornar.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => void handleSubmit()}
          disabled={estornar.isPending}
        >
          {estornar.isPending
            ? t('common.saving')
            : t('features.requisicoesCompra.extrato.estorno.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
